import React, { memo, useCallback, useEffect, useMemo, Dispatch } from 'react';
import { connect, ConnectedProps } from 'react-redux';
import EditFieldView from './EditFieldView';
import { useLangFile } from '../../context/LanguageContext';
import { Box, MenuItem, Typography } from '@mui/material';
import { BorderClear, CallSplit, ChevronRight } from '@mui/icons-material';
import { setViewMode } from 'js/reducers/ControlReducer';
import viewModeConstants from '../../constants/ViewModeConstants';
import EditIcon from '@mui/icons-material/Edit';
import MergeIcon from '@mui/icons-material/MergeType';
import { setIsEditingFieldBoundaries, setIsSplittingField } from '../../reducers/FieldReducer';
import { fitMapToBounds } from '../../helpers/MapsUtils';

const mapStoreToProps = (store: any) => ({
  selectedField: store.field.selectedField,
});

type PropsFromRedux = ConnectedProps<typeof connector>;

interface EditFieldViewContainerProps extends PropsFromRedux {
  googleMap: google.maps.Map;
  dispatch: Dispatch<any>;
}

const EditFieldViewContainer: React.FC<EditFieldViewContainerProps> = ({
  selectedField,
  googleMap,
  dispatch,
}) => {
  const LangFile = useLangFile();

  const menuItems: { label: string; icon: React.FC; action: () => void }[] = useMemo(
    (): { label: string; icon: React.FC; action: () => void }[] => [
      {
        label: LangFile.EditFieldMenu.addEditInnerBoundaries,
        icon: <BorderClear fontSize={'small'} color={'primary'} />,
        action: () => dispatch(setViewMode(viewModeConstants.ADD_EDIT_INNERBOUNDARIES)),
      },
      {
        label: LangFile.EditFieldMenu.editFieldBoundaries,
        icon: <EditIcon fontSize={'small'} color={'primary'} />,
        action: () => {
          dispatch(setIsEditingFieldBoundaries(true));
          dispatch(setViewMode(viewModeConstants.EDIT_FIELD_BOUNDARIES));
        },
      },
      {
        label: LangFile.EditFieldMenu.splitField,
        icon: <CallSplit fontSize={'small'} color={'primary'} />,
        action: () => {
          dispatch(setIsSplittingField(true));
          dispatch(setViewMode(viewModeConstants.SPLIT_FIELD));
        },
      },
      {
        label: LangFile.EditFieldMenu.mergeFields,
        icon: <MergeIcon fontSize={'small'} color={'primary'} />,
        action: () => dispatch(setViewMode(viewModeConstants.MERGE_FIELDS)),
      },
    ],
    [LangFile]
  );

  const zoomToField: () => void = useCallback(() => {
    if (!selectedField || !googleMap) return;
    fitMapToBounds(googleMap, selectedField.bounds);
  }, [googleMap, selectedField]);

  const FieldEditMenuItems: React.FC<{ onClick: () => void; text: string; icon: React.FC }> = ({
    onClick,
    text,
    icon,
  }) => (
    <MenuItem onClick={onClick}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
        }}
      >
        {icon && (
          <Box
            sx={{
              mr: 1,
              display: 'flex',
              alignItems: 'center',
            }}
          >
            {icon}
          </Box>
        )}
        <Typography>{text}</Typography>
      </Box>
      <Box
        sx={{
          my: 0.5,
          flex: 1,
          display: 'flex',
          justifyContent: 'flex-end',
        }}
      >
        <ChevronRight />
      </Box>
    </MenuItem>
  );

  useEffect(() => {
    if (selectedField) {
      zoomToField();
    }
  }, [selectedField, zoomToField]);

  return (
    <EditFieldView
      menuItems={menuItems.map(({ label, icon, action }) => (
        <FieldEditMenuItems key={label} onClick={action} text={label} icon={icon} />
      ))}
    />
  );
};

const connector = connect(mapStoreToProps);
export default memo(connector(EditFieldViewContainer));
